/**
 * Recent Activity Table component for dashboard
 * 
 * Displays a table of recent activity events.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon, type IconName } from '@/components/ui/icon';
import { formatRelativeTime, cn } from '@/lib/utils';

export interface ActivityItem {
  id: string;
  type: 'agent_activated' | 'agent_created' | 'documents_ingested' | 'conversation_started' | 'error' | string;
  description: string;
  timestamp: string;
  agentName?: string;
  userName?: string;
}

interface RecentActivityTableProps {
  data: ActivityItem[];
  title?: string;
}

const typeConfig: Record<string, { icon: IconName; color: string }> = {
  agent_activated: { icon: 'bot', color: 'text-green-500' },
  agent_created: { icon: 'plus', color: 'text-blue-500' },
  documents_ingested: { icon: 'file-text', color: 'text-purple-500' },
  conversation_started: { icon: 'message', color: 'text-cyan-500' },
  error: { icon: 'alert-circle', color: 'text-red-500' },
};

export function RecentActivityTable({
  data,
  title = 'Recent Activity',
}: RecentActivityTableProps) {
  if (data.length === 0) {
    return (
      <Card data-testid="recent-activity">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground" data-testid="recent-activity-empty">
            No recent activity
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="recent-activity">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4" data-testid="recent-activity-list">
          {data.map((item) => {
            const config = typeConfig[item.type] || { icon: 'activity' as IconName, color: 'text-gray-500' };
            
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 pb-4 border-b last:border-0 last:pb-0"
                data-testid="activity-item"
              >
                <div className={cn('mt-0.5', config.color)}>
                  <Icon name={config.icon} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.agentName && (
                      <span className="text-xs text-muted-foreground">
                        Agent: {item.agentName}
                      </span>
                    )}
                    {item.userName && (
                      <span className="text-xs text-muted-foreground">
                        User: {item.userName}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeTime(item.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
